import { useCallback, useEffect, useState } from 'react';
import { Card, DataTable, FilterBar, Select, Modal, FormField, FormSelect, TextInput, StatusBadge, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface PoolDid {
  id: number;
  number: string;
  type: string;
  tenant_id?: number | null;
  tenant_name?: string | null;
  trunk_id?: number | null;
  trunk_name?: string | null;
  route_type?: string | null;
  route_target?: string | null;
  monthly_price_cents?: number;
  purchase_price_cents?: number;
}
interface TenantOpt { id: number; name: string }
interface TrunkOpt { id: number; name: string }

const TYPE_LABEL: Record<string, string> = { landline: 'קווי', virtual: 'וירטואלי', mobile: 'נייד', tollfree: 'חינם' };
const ROUTE_LABEL: Record<string, string> = { queue: 'תור', ivr: 'IVR', ext: 'שלוחה', voicemail: 'תא קולי' };

export function DidPool() {
  const [rows, setRows] = useState<PoolDid[]>([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const [trunks, setTrunks] = useState<TrunkOpt[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<PoolDid | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PoolDid | null>(null);
  const [priceTarget, setPriceTarget] = useState<PoolDid | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superApi.didPool({ status: status || undefined, q: q || undefined });
      setRows(res.data as PoolDid[]);
    } catch {
      toast('שגיאה בטעינת המאגר', 'danger');
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    void superApi.tenants({ per_page: 200 }).then((r) => setTenants((r.data as TenantOpt[]) ?? [])).catch(() => {});
    void superApi.trunks().then((t) => setTrunks((t as TrunkOpt[]) ?? [])).catch(() => {});
  }, []);

  async function unassign(d: PoolDid) {
    try {
      await superApi.unassignDid(d.id);
      toast('המספר שוחרר', 'ok');
      void load();
    } catch {
      toast('שגיאה בשחרור', 'danger');
    }
  }

  async function remove(d: PoolDid) {
    try {
      await superApi.deleteDid(d.id);
      toast('נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== d.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const cols: DataTableColumn<PoolDid>[] = [
    { key: 'number', he: 'מספר', render: (r) => <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{r.number}</span> },
    { key: 'type', he: 'סוג', render: (r) => TYPE_LABEL[r.type] ?? r.type },
    {
      key: 'tenant',
      he: 'לקוח',
      render: (r) => r.tenant_id
        ? <StatusBadge tone="ok" size="sm" dot>{r.tenant_name ?? `#${r.tenant_id}`}</StatusBadge>
        : <StatusBadge tone="neutral" size="sm">פנוי</StatusBadge>,
    },
    {
      key: 'trunk',
      he: 'קו (Trunk)',
      render: (r) => <span style={{ fontSize: 'var(--t-xs)' }}>{r.trunk_name ?? 'Mvoice (משותף)'}</span>,
    },
    {
      key: 'pricing',
      he: 'תמחור',
      render: (r) => (
        <span style={{ fontSize: 'var(--t-xs)' }}>
          ₪{((r.monthly_price_cents ?? 0) / 100).toLocaleString('he-IL')}/חודש
          {(r.purchase_price_cents ?? 0) > 0 && <> + ₪{((r.purchase_price_cents ?? 0) / 100).toLocaleString('he-IL')} קנייה</>}
        </span>
      ),
    },
    {
      key: 'route',
      he: 'ניתוב',
      render: (r) => r.route_type ? `${ROUTE_LABEL[r.route_type] ?? r.route_type}${r.route_target ? ` · ${r.route_target}` : ''}` : '—',
    },
    {
      key: '_actions',
      he: '',
      align: 'end',
      render: (r) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn" onClick={() => setPriceTarget(r)}>תמחור</button>
          {r.tenant_id ? (
            <button type="button" className="btn ghost" onClick={() => unassign(r)}>שחרר</button>
          ) : (
            <>
              <button type="button" className="btn primary" onClick={() => setAssignTarget(r)}>הקצה</button>
              <button type="button" className="btn danger" onClick={() => setDeleteTarget(r)}>מחק</button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="מאגר מספרים" actions={<>
        <button type="button" className="btn primary" onClick={() => setAddOpen(true)}>+ הוסף מספרים</button>
      </>} />

      <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} placeholder="חיפוש מספר…">
        <Select value={status} onChange={setStatus} options={[
          { value: '', label: 'הכל' },
          { value: 'available', label: 'פנויים' },
          { value: 'allocated', label: 'מוקצים' },
        ]} />
      </FilterBar>

      <Card padded={false}><DataTable<PoolDid> columns={cols} rows={rows} loading={loading} /></Card>

      {addOpen && (
        <AddNumbers
          trunks={trunks}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); void load(); }}
        />
      )}

      {assignTarget && (
        <AssignDid
          did={assignTarget}
          tenants={tenants}
          trunks={trunks}
          onClose={() => setAssignTarget(null)}
          onSaved={() => { setAssignTarget(null); void load(); }}
        />
      )}

      {priceTarget && (
        <PricingModal
          did={priceTarget}
          onClose={() => setPriceTarget(null)}
          onSaved={() => { setPriceTarget(null); void load(); }}
        />
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)} title="מחיקת מספר"
          footer={<><button type="button" className="btn" onClick={() => setDeleteTarget(null)}>ביטול</button><button type="button" className="btn danger" onClick={() => remove(deleteTarget)}>מחק</button></>}>
          <p>למחוק את המספר <strong dir="ltr">{deleteTarget.number}</strong> מהמאגר?</p>
        </Modal>
      )}
    </div>
  );
}

function AddNumbers({ trunks, onClose, onSaved }: { trunks: TrunkOpt[]; onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState('');
  const [type, setType] = useState('virtual');
  const [trunkId, setTrunkId] = useState('');
  // תמחור (₪ ב-UI, אגורות ב-API) — מוצמד לכל המספרים שנוספים יחד.
  const [monthly, setMonthly] = useState('');
  const [purchase, setPurchase] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    const numbers = text.split(/[\s,;]+/).map((n) => n.trim()).filter(Boolean);
    if (numbers.length === 0) {
      toast('יש להזין לפחות מספר אחד', 'danger');
      return;
    }
    setSaving(true);
    const trunk_id = trunkId ? Number(trunkId) : null;
    try {
      const pricing = {
        monthly_price_cents: monthly !== '' ? Math.round(Number(monthly) * 100) : 0,
        purchase_price_cents: purchase !== '' ? Math.round(Number(purchase) * 100) : 0,
      };
      const res = await superApi.addDids(numbers.length === 1
        ? { number: numbers[0], type, trunk_id, ...pricing }
        : { numbers, type, trunk_id, ...pricing });
      toast(`נוספו ${res.added} מספרים${res.skipped ? `, ${res.skipped} כבר קיימים` : ''}`, 'ok');
      onSaved();
    } catch {
      toast('שגיאה בהוספה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="הוספת מספרים למאגר"
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'הוסף'}</button></>}>
      <div className="stack">
        <FormField label="מספרים" hint="מספר אחד או רשימה (מופרדים ברווח, פסיק או שורה חדשה)">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            dir="ltr"
            rows={5}
            placeholder={'0732745593\n0732745594'}
            style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)', fontFamily: 'var(--font-mono)', resize: 'vertical' }}
          />
        </FormField>
        <FormField label="סוג">
          <FormSelect value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FormSelect>
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="עלות חודשית (₪)" hint="מצטרפת לחיוב החודשי של העסק שיתפוס את המספר.">
            <TextInput type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} dir="ltr" placeholder="0" />
          </FormField>
          <FormField label="עלות קנייה (₪)" hint="נגבית חד-פעמית בחודש התפיסה.">
            <TextInput type="number" value={purchase} onChange={(e) => setPurchase(e.target.value)} dir="ltr" placeholder="0" />
          </FormField>
        </div>
        <FormField label="קו (Trunk)" hint="דרכו המספרים נכנסים/יוצאים. ברירת מחדל: Mvoice המשותף">
          <FormSelect value={trunkId} onChange={(e) => setTrunkId(e.target.value)}>
            <option value="">Mvoice (משותף)</option>
            {trunks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormSelect>
        </FormField>
      </div>
    </Modal>
  );
}

function AssignDid({ did, tenants, trunks, onClose, onSaved }: {
  did: PoolDid;
  tenants: TenantOpt[];
  trunks: TrunkOpt[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [trunkId, setTrunkId] = useState(did.trunk_id ? String(did.trunk_id) : '');
  const [routeType, setRouteType] = useState('');
  const [routeTarget, setRouteTarget] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!tenantId) {
      toast('יש לבחור לקוח', 'danger');
      return;
    }
    setSaving(true);
    try {
      await superApi.assignDid(did.id, Number(tenantId), {
        trunk_id: trunkId ? Number(trunkId) : null,
        ...(routeType
          ? { route_type: routeType, route_target: routeType !== 'voicemail' ? routeTarget || null : null }
          : {}),
      });
      toast('המספר הוקצה', 'ok');
      onSaved();
    } catch {
      toast('שגיאה בהקצאה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`הקצאת ${did.number}`}
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'הקצה'}</button></>}>
      <div className="stack">
        <FormField label="לקוח" required>
          <FormSelect value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">— בחר לקוח —</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormSelect>
        </FormField>
        <FormField label="קו (Trunk)" hint="דרכו השיחות נכנסות/יוצאות">
          <FormSelect value={trunkId} onChange={(e) => setTrunkId(e.target.value)}>
            <option value="">Mvoice (משותף)</option>
            {trunks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormSelect>
        </FormField>
        <FormField label="ניתוב שיחות נכנסות" hint="אופציונלי — אפשר להגדיר מאוחר יותר במסך הלקוח">
          <FormSelect value={routeType} onChange={(e) => { setRouteType(e.target.value); setRouteTarget(''); }}>
            <option value="">— ללא ניתוב —</option>
            {Object.entries(ROUTE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FormSelect>
        </FormField>
        {routeType && routeType !== 'voicemail' && (
          <FormField label="יעד (שלוחת תור / מזהה IVR / שלוחה)">
            <TextInput value={routeTarget} onChange={(e) => setRouteTarget(e.target.value)} dir="ltr" placeholder="200" />
          </FormField>
        )}
      </div>
    </Modal>
  );
}


/** עריכת התמחור של מספר קיים — חל גם על מספרים שכבר משויכים לעסק. */
function PricingModal({ did, onClose, onSaved }: { did: PoolDid; onClose: () => void; onSaved: () => void }) {
  const [monthly, setMonthly] = useState(String((did.monthly_price_cents ?? 0) / 100));
  const [purchase, setPurchase] = useState(String((did.purchase_price_cents ?? 0) / 100));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await superApi.updateDidPricing(did.id, {
        monthly_price_cents: Math.round(Number(monthly || 0) * 100),
        purchase_price_cents: Math.round(Number(purchase || 0) * 100),
      });
      toast('התמחור עודכן', 'ok');
      onSaved();
    } catch {
      toast('שגיאה בעדכון התמחור', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`תמחור ${did.number}`}
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button></>}>
      <div className="stack">
        <FormField label="עלות חודשית (₪)" hint="מצטרפת לחיוב החודשי של העסק המחזיק במספר.">
          <TextInput type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} dir="ltr" />
        </FormField>
        <FormField label="עלות קנייה (₪)" hint="נגבית חד-פעמית בחודש שבו העסק תופס את המספר.">
          <TextInput type="number" value={purchase} onChange={(e) => setPurchase(e.target.value)} dir="ltr" />
        </FormField>
      </div>
    </Modal>
  );
}
